import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSection } from "@/context/SiteContentContext";
import client from "@/lib/api";
import SocialIcons from "@/components/SocialIcons";

const FALLBACK = {
  logo_url: "https://customer-assets.emergentagent.com/job_collection-catalog/artifacts/n8hgnnc8_mws.jpg",
  tagline: "Independent apparel studio. Designed in Norwich, UK, printed in limited runs for those who climb their own mountain.",
  newsletter_title: "Newsletter",
  newsletter_blurb: "Drops, custom slots, and studio dispatches.",
  copyright_suffix: "MotoWear Studio Ltd.",
  location: "Norwich, UK / Made independently",
};

export default function Footer() {
  const c = useSection("footer", FALLBACK);
  const [collections, setCollections] = useState([]);
  useEffect(() => {
    client.get("/themes").then((r) => setCollections((r.data || []).slice(0, 6))).catch(() => {});
  }, []);
  return (
    <footer className="border-t border-black/10 mt-32" data-testid="site-footer">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-20 grid grid-cols-1 md:grid-cols-5 gap-12">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3">
            <img
              src={c.logo_url}
              alt="MotoWear Studio — limited-run garments from Norwich, UK"
              className="h-12 w-auto"
              draggable="false"
              width="120"
              height="48"
            />
          </div>
          <p className="mt-6 text-sm text-black/60 max-w-xs leading-relaxed">{c.tagline}</p>
          <address className="not-italic mt-6 text-xs text-black/45 leading-relaxed">
            <a href="mailto:service@motowear.co.uk" className="hover:text-black">service@motowear.co.uk</a><br/>
            <a href="tel:+447960551154" className="hover:text-black">07960 551154</a><br/>
            Norwich, UK
          </address>
          <div className="mt-6" data-testid="footer-socials">
            <SocialIcons variant="outline" size="md" testidPrefix="footer-social" />
          </div>
        </div>
        <nav aria-label="Shop by type">
          <div className="label-eyebrow text-black/40 mb-4">Shop</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/shop?type=t-shirt" className="hover:moto-accent">T-Shirts</Link></li>
            <li><Link to="/shop?type=hoodie" className="hover:moto-accent">Hoodies</Link></li>
            <li><Link to="/shop?type=hat" className="hover:moto-accent">Hats</Link></li>
            <li><Link to="/shop?type=sweatshirt" className="hover:moto-accent">Sweatshirts</Link></li>
            <li><Link to="/shop" className="hover:moto-accent">All garments</Link></li>
            <li><Link to="/accessories" className="hover:moto-accent" data-testid="footer-accessories-link">Accessories</Link></li>
          </ul>
        </nav>
        <nav aria-label="Browse by collection">
          <div className="label-eyebrow text-black/40 mb-4">Collections</div>
          <ul className="space-y-2 text-sm" data-testid="footer-collections">
            {collections.map((coll) => (
              <li key={coll.id}>
                <Link to={`/collection/${coll.slug}`} className="hover:moto-accent">{coll.name}</Link>
              </li>
            ))}
            <li><Link to="/collections" className="hover:moto-accent">All collections</Link></li>
          </ul>
        </nav>
        <nav aria-label="Studio">
          <div className="label-eyebrow text-black/40 mb-4">Studio</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/custom" className="hover:moto-accent">Custom Design</Link></li>
            <li><Link to="/bespoke" className="hover:moto-accent">Bespoke</Link></li>
            <li><Link to="/bulk-orders" className="hover:moto-accent" data-testid="footer-bulk-orders-link">Bulk Orders</Link></li>
            <li><Link to="/trade-account" className="hover:moto-accent" data-testid="footer-trade-link">Trade Accounts</Link></li>
            <li><Link to="/our-studio" className="hover:moto-accent" data-testid="footer-studio-link">Our Studio</Link></li>
            <li><Link to="/club" className="hover:moto-accent">Moto Club</Link></li>
            <li><Link to="/reviews" className="hover:moto-accent" data-testid="footer-reviews-link">Reviews</Link></li>
            <li><Link to="/style-quiz" className="hover:moto-accent" data-testid="footer-style-quiz-link">Style Quiz — 10% off</Link></li>
            <li><Link to="/share-your-story" className="hover:moto-accent" data-testid="footer-share-story-link">Share Your Story</Link></li>
            <li><Link to="/about" className="hover:moto-accent">About</Link></li>
            <li><Link to="/contact" className="hover:moto-accent">Contact</Link></li>
            <li><Link to="/faq" className="hover:moto-accent">FAQ</Link></li>
            <li><Link to="/overseas-quote" className="hover:moto-accent">Overseas Shipping Quote</Link></li>
          </ul>
        </nav>
      </div>
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 pb-12 grid grid-cols-1 md:grid-cols-2 gap-8 items-end">
        <div>
          <div className="label-eyebrow text-black/40 mb-4">{c.newsletter_title}</div>
          <p className="text-sm text-black/60 mb-4 max-w-md">{c.newsletter_blurb}</p>
          <form onSubmit={(e) => e.preventDefault()} className="flex gap-0 max-w-md">
            <input type="email" placeholder="email@studio.com" className="flex-1 border-b border-black bg-transparent py-2 text-sm focus:outline-none" data-testid="newsletter-input" aria-label="Email address for newsletter" />
            <button className="label-eyebrow border-b border-black px-3" data-testid="newsletter-submit" aria-label="Subscribe to newsletter">→</button>
          </form>
        </div>
        <div className="md:text-right text-xs text-black/45 leading-relaxed">
          <div className="label-eyebrow text-black/40 mb-3">Causes we support</div>
          <div className="flex md:justify-end flex-wrap gap-x-4 gap-y-1 mb-6" data-testid="footer-charities">
            <a href="https://www.shelter.org.uk/" target="_blank" rel="noopener noreferrer" className="hover:moto-accent" data-testid="footer-charity-shelter">Shelter</a>
            <a href="https://www.trusselltrust.org/" target="_blank" rel="noopener noreferrer" className="hover:moto-accent" data-testid="footer-charity-trussell">Trussell Trust</a>
            <a href="https://www.crisis.org.uk/" target="_blank" rel="noopener noreferrer" className="hover:moto-accent" data-testid="footer-charity-crisis">Crisis</a>
            <a href="https://www.thebigissue.com/" target="_blank" rel="noopener noreferrer" className="hover:moto-accent" data-testid="footer-charity-bigissue">The Big Issue</a>
            <a href="https://www.foodbank.org.uk/" target="_blank" rel="noopener noreferrer" className="hover:moto-accent" data-testid="footer-charity-foodbank">Food Bank Network</a>
            <a href="https://centrepoint.org.uk/" target="_blank" rel="noopener noreferrer" className="hover:moto-accent" data-testid="footer-charity-centrepoint">Centrepoint</a>
            <a href="https://www.fareshare.org.uk/" target="_blank" rel="noopener noreferrer" className="hover:moto-accent" data-testid="footer-charity-fareshare">FareShare</a>
            <a href="https://www.stmungos.org/" target="_blank" rel="noopener noreferrer" className="hover:moto-accent" data-testid="footer-charity-stmungos">St Mungo&#39;s</a>
          </div>
          <div className="label-eyebrow text-black/40 mb-3">Studio recognition</div>
          <div className="flex md:justify-end flex-wrap gap-x-4 gap-y-1">
            <a href="https://www.madeinbritain.org/" target="_blank" rel="noopener noreferrer" className="hover:moto-accent">Made in Britain</a>
            <a href="https://commonobjective.co/" target="_blank" rel="noopener noreferrer" className="hover:moto-accent">Common Objective</a>
            <a href="https://www.royalmail.com/track-your-item" target="_blank" rel="noopener noreferrer" className="hover:moto-accent">Royal Mail Tracking</a>
            <a href="https://www.gov.uk/consumer-protection-rights" target="_blank" rel="noopener noreferrer" className="hover:moto-accent">UK Consumer Rights</a>
          </div>
        </div>
      </div>
      <div className="border-t border-black/10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row justify-between gap-4 text-xs text-black/50">
          <div className="flex flex-col gap-1">
            <div>© {new Date().getFullYear()} {c.copyright_suffix} · All images © Moto Wear Studios</div>
            <div className="text-black/45" data-testid="footer-bulk-callout">Available for bespoke commissions and bulk printing orders across the UK.</div>
          </div>
          <div className="flex flex-wrap gap-4">
            <Link to="/terms" className="hover:moto-accent">Terms & Conditions</Link>
            <Link to="/privacy" className="hover:moto-accent">Privacy Policy</Link>
            <span>{c.location}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
